import { useProfile } from 'context/profile';

/**
 * Outbound-referral wording, per the viewing organization (OLIO-2905).
 *
 * "Outbound" only reads correctly from the sending organization's point of view — a home
 * health or hospice agency *receives* the referrals it sees, so the neutral "Active
 * Referrals" wording stays for them. A client that both sends and receives is labeled as a
 * sender; full inbound/outbound parity for receiving organizations is deferred.
 *
 * Note: this is unrelated to the "Active Referrals" tab inside the Inbound Referrals queue,
 * which is a status filter and must not pick up this wording.
 */
export function useReferralTerminology() {
  const { profile } = useProfile();
  const sendsReferrals = profile.actingClient.sendsReferrals;

  return {
    sendsReferrals,
    // Nav item, list page title, and empty-state heading noun
    listLabel: sendsReferrals ? 'Outbound Referrals' : 'Active Referrals',
    // Patient story tab
    tabLabel: sendsReferrals ? 'Outbound Referral' : 'Referral',
    // Lowercase nouns for sentence-case copy. `noun` is the plural form used where the count
    // is not adjacent or is always plural (FilterBar's resultsText, "N outbound referrals
    // found"); pair it with `nounSingular` anywhere a count of one is reachable.
    noun: sendsReferrals ? 'outbound referrals' : 'referrals',
    nounSingular: sendsReferrals ? 'outbound referral' : 'referral',
    // Empty state. The receiving-organization strings are the pre-OLIO-2905 copy verbatim —
    // "No Active Referrals" would read worse than the "No Referrals" it replaced.
    emptyStateHeading: sendsReferrals ? 'No Outbound Referrals' : 'No Referrals',
    emptyStateBody: sendsReferrals
      ? 'Any outbound referrals in Olio will be listed here'
      : 'Any active referrals in Olio will be listed here',
  };
}

export default useReferralTerminology;
